import { useState, useEffect, useCallback, useRef, useContext, version } from "react";
import Editor from "@monaco-editor/react";
import { FiFileText } from "react-icons/fi";
import AgentAiChat from "../AIChat/AgentAiChat";
import { UNSAFE_NavigationContext, useNavigate, useParams } from "react-router-dom";
import { FaDocker, FaNodeJs, FaPython } from "react-icons/fa";
import { VscJson, VscSettingsGear } from "react-icons/vsc";
import PublishMcpModal from "./PublishMcpModal";
import CodeTreeLoader from "../Loaders/CodeTreeLoader";
import InfoModal from "./InfoModal";
import { io } from "socket.io-client";
import EditorTerminal from "./EditorTerminal";
import { BiRocket, BiSave } from "react-icons/bi";
import SaveMcpModal from "./SaveMcpModal";
import Spinner from "../Components/Spinner";
import VersioningLoader from "../Loaders/VersioningLoader";
import UnsavedChangesModal from "./UnsavedChangesModal";
import CircleBackground from "../Components/CircleBackground";
import ConfirmDeleteMcpModal from "./ConfirmDeleteMcpModal";
import PopupMex from "../Components/SavePopup";
import { getLastWordsContext } from "../Functions/GetLastWords"
import { TbLayoutSidebarLeftExpand, TbLayoutSidebarRightExpandFilled } from "react-icons/tb";
import ConnectCustomMcpModal from "./CustomMcps/ConnectCustomMcpModal";
import DisconnectCustomMcpModal from "./CustomMcps/DisconnectCustomMcpModal";
import { BsInfo } from "react-icons/bs";
import { useLanguage } from "../Translations/LenguageContext";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;
const GEN_AI_LINK = process.env.REACT_APP_GEN_AI_LINK
const REACT_APP_GEN_BACKEND_LINK_STREAMING = process.env.REACT_APP_GEN_BACKEND_LINK_STREAMING


const McpBuilder = ({
    userData,
    account_id,
    singleOrgData,
}) => {
    const {t} = useLanguage()
    const setupRef = useRef(false);
    const navigate = useNavigate()
    const inputRef = useRef(null);
    const socketRef = useRef(null);
    const { org_id, runtime, mcp_id, version_n } = useParams()
    const [openAiChat, setOpenAichat] = useState(true);
    const [editorTheme, setEditorTheme] = useState("vs-dark");
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [errorAi, setErrorAi] = useState({ message: "", display: false });
    const chatEndRef = useRef(null);
    const [chatId, setChatId] = useState(null);
    const abortControllerRef = useRef(null);
    const editorRef = useRef(null);
    const [originalFiles, setOriginalFiles] = useState({})
    const [files, setFiles] = useState({});
    const [filesToSave, setFilesToSave] = useState({});
    const [activeFile, setActiveFile] = useState("");
    const [draftedVersion, setDraftedVersion] = useState("")
    const [openPublishModal, setOpenPublishModal] = useState(false)
    const [loadingMcpData, setLoadingMcpData] = useState(true)
    const [mcpData, setMcpData] = useState([]);
    const [openTerminal, setOpenTerminal] = useState(false)
    const [mcpLogs, setMcpLogs] = useState([])
    const [logLoading, setLogLoading] = useState(false)
    const [openInfoModal, setOpenInfoModal] = useState(false)
    const [statusMcp, setStatusMcp] = useState("LOADING");
    const [openSaveModal, setOpenSaveModal] = useState(false)
    const [openVersions, setOpenVersions] = useState(true)
    const [versions, setVersions] = useState([])
    const [chatTitle, setChatTitle] = useState("")
    const [loadingChatHistory, setLoadingChatHistory] = useState(false)
    const [loadingChats, setLoadingChats] = useState(false)
    const [chatData, setChatData] = useState([]);
    const [steps, setSteps] = useState({})
    const [loadingSave, setLoadingSave] = useState(false)
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
    const [openAlert, setOpenAlert] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const skipPromptRef = useRef(false);
    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false)
    const [popupMex, setPopupMex] = useState({ active: false, type: null, message: "" })
    const [openManually, setOpenManually] = useState(false)
    const [isSearchEnabled, setIsSearchEnabled] = useState(false)
    const [isAutotestEnabled, setIsAutotestEnabled] = useState(false)
    const [selectedModel, setSelectedModel] = useState("cerebras/gpt-oss-120b")
    const [providerModel, setProviderModel] = useState("cerebras")

    const [selectedMCP, setSelectedMcp] = useState(null)
    const [openErrDepModal, setOpenErrDepModal] = useState(false)
    const [openCustomModal, setOpenCustomModal] = useState(false)
    const [showCustomDisconnectModal, setShowCustomDisconnectModal] = useState(false)

    const [loadingRunTest, setLoadingRunTest] = useState(false)

    useEffect(()=> {
        if(mcp_id.startsWith("pub-abty")){
            navigate(`/e8console/organizations/${org_id}/abilities`)
        }
    }, [mcp_id])


    const handleOpenConnect = (system_provider) => {
        setOpenCustomModal(true)
    }

    const handleOpenDisconnect = (system_provider) => {
        setShowCustomDisconnectModal(true)
    }

    const getEditorLanguage = () => {
        if (!activeFile) return "plaintext";
        if (activeFile.endsWith(".py")) return "python";
        if (activeFile.endsWith(".js")) return "javascript";
        if (activeFile.endsWith(".json")) return "json";
        return "plaintext";
    };

    const resolveTheme = useCallback((pref) => {
        const theme = pref || localStorage.getItem("pe8-theme-preference") || "system";
        if (theme === "dark") return "vs-dark";
        if (theme === "light") return "light";
        return window.matchMedia("(prefers-color-scheme: dark)").matches ? "vs-dark" : "light";
    }, []);

    const toggleCodeView = () => setOpenAichat(!openAiChat);

    const handleSendMessage = async (e) => {
        if (e) e.preventDefault();
        if (!input.trim() || isLoading) return;

        setErrorAi({ message: "", display: false });
        setIsLoading(true);
        const currentInput = input;
        setInput("");
        setSteps({})

        if (inputRef?.current) {
            inputRef.current.style.height = "auto";
            inputRef.current.style.overflowY = "hidden";
        }

        const lastContext = getLastWordsContext(messages, 1000);

        const userMessage = {
            id: Date.now(),
            sender: "user",
            text: currentInput,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        };

        const aiMessageId = Date.now() + 1;
        const aiPlaceholder = {
            id: aiMessageId,
            sender: "ai",
            model_used: selectedModel,
            text: "",
            reasoning_depth: "Unset",
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        };

        setMessages((prev) => [...prev, userMessage, aiPlaceholder]);
        const code = files || {};

        abortControllerRef.current = new AbortController();

        try {
            const response = await fetch(`${GEN_AI_LINK}/mcp_builder_ai`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                signal: abortControllerRef.current.signal,
                body: JSON.stringify({
                    account_id,
                    org_id,
                    mcp_id,
                    chat_id: chatId,
                    chat_title: chatTitle,
                    model: selectedModel,
                    inference_provider: providerModel,
                    prompt: currentInput,
                    last_prompts: lastContext,
                    code,
                    runtime,
                    is_search_enabled: isSearchEnabled,
                    is_autotest_enabled: isAutotestEnabled,
                }),
            });


            if (!response.ok) throw new Error("Failed to connect to AI");

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let accumulatedText = "";
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                const lines = chunk.split("\n");

                for (const line of lines) {
                    if (!line.startsWith("data:")) continue;

                    try {
                        const jsonStr = line.replace("data:", "").trim();
                        if (!jsonStr) continue;

                        const data = JSON.parse(jsonStr);

                        if (data.type == "setup") {
                            setChatId(data.chat_id);
                            setChatTitle(data.chat_title);
                            setupRef.current = true;
                        }
                        else if (data.type === "token") {
                            setIsLoading(false);
                            accumulatedText += data.text;

                            setMessages((prev) =>
                                prev.map((msg) =>
                                    msg.id === aiMessageId
                                        ? { ...msg, text: accumulatedText }
                                        : msg
                                )
                            );
                        }

                        else if (data.type === "error") {
                            throw new Error(data.msg);
                        }

                    } catch (err) {
                        console.error("SSE parse error:", err);
                    }
                }
            }

        } catch (error) {
            if (error.name === 'AbortError') {
                console.log("Stream aborted");
            } else {
                console.error("Error sending message:", error);
                setErrorAi({ message: t.playground.error_gen, display: true });
            }
        } finally {
            abortControllerRef.current = null;

            if (setupRef.current) {
                fetchChatHistory();
                setupRef.current = false;
            }
        }
    };

    const handleStopMessage = async () => {
        if (abortControllerRef.current) abortControllerRef.current.abort();
        setIsLoading(false);

        if (chatId) {
            try {
                await fetch(`${GEN_AI_LINK}/stop_chat_builder`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(
                        {
                            account_id,
                            chat_id: chatId
                        }
                    ),
                });
            } catch (err) {
                console.error("Failed to stop AI chat:", err);
            }
        }
    };

    const fetchSingleChat = async (selectedChatId) => {
        if (!selectedChatId) {
            setMessages([])
            return
        }

        setLoadingChatHistory(true)
        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_single_chat`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id,
                    chat_id: selectedChatId
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch chat history");
            }

            const chatData = result.data;

            if (!chatData || !chatData.messages) {
                setMessages([]);
                return;
            }

            const sortedMessages = [...chatData.messages].sort((a, b) =>
                new Date(a.created_at) - new Date(b.created_at)
            );

            const mappedMessages = sortedMessages.map(msg => ({
                id: msg.message_id || Date.now() + Math.random(),
                sender: msg.message?.role === "user" ? "user" : "ai",
                text: msg.message?.content || "",
                timestamp: new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                model_used: msg.config?.model,
                reasoning_flow: msg.reasoning?.steps || [],
                reasoning_depth: msg.config?.reasoning_depth || [],
                search_metadata: msg.search_results || null
            }));

            setMessages(mappedMessages);
            setChatId(chatData.chat_id || selectedChatId);
            setChatTitle(chatData.chat_name || "");

        } catch (err) {
            console.error("Error fetching chat history:", err);
            setMessages([]);
            setLoadingChatHistory(false)
        } finally {
            setTimeout(() => {
                setLoadingChatHistory(false)
            }, 500);
        }
    };

    const fetchChatHistory = async () => {
        setLoadingChats(true)
        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_chat_history`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id,
                    org_id,
                    type: mcp_id
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            if (result.data.length == 0) {
                setMessages([])
            }
            setChatData(result.data)
        } catch (err) {
            console.error("Error fetching models:", err);
            setLoadingChats(false)
        } finally {
            setTimeout(() => {
                setLoadingChats(false)
            }, 500);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleEditorChange = (value) => {
        setFiles((prev) => ({
            ...prev,
            [activeFile]: value
        }));
    };

    const handleOpenPublishModal = () => {
        const filesArray = Object.keys(files).map((filename) => ({
            filename,
            content: files[filename]
        }));

        setFilesToSave(filesArray);
        setOpenPublishModal(true);
    }

    const fetchMcpData = async () => {
        if(mcp_id.startsWith("pub-abty")) return;
        setLoadingMcpData(true)
        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_single_mcp`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id,
                    org_id,
                    mcp_id,
                    version: version_n
                }),
            });

            const result = await response.json();

            if (result.error == "not_authorized") {
                setPopupMex({ active: true, type: "error", message: "Organization auth..." })
                setTimeout(() => {
                    setPopupMex({ active: false, type: null, message: "" })
                    navigate(`/e8console/organizations`)
                }, 5000);
            }

            if (result.error == "not_found") {
                setPopupMex({ active: true, type: "error", message: "Ability not found..." })
                setTimeout(() => {
                    setPopupMex({ active: false, type: null, message: "" })
                    navigate(`/e8console/organizations/${org_id}/abilities`)
                }, 5000);
            }

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            if (result.status === "success" && result.files) {
                setMcpData(result.data || []);
                setVersions(result.versions)
                setFiles(result.files);
                setOriginalFiles(result.files)
                const new_drafted_version = result.data.drafted_version
                setDraftedVersion(new_drafted_version)

                if ((new_drafted_version > (Number(version_n)) && !openManually)) {
                    const mainUrl = `/e8console/organizations/${org_id}/abilities`;
                    const targetPath =
                        runtime === "python"
                            ? `${mainUrl}/ability-builder/python/${mcp_id}/${new_drafted_version}`
                            : `${mainUrl}/ability-builder/javascript/${mcp_id}/${new_drafted_version}`;

                    navigate(targetPath);
                }
            }
        } catch (err) {
            console.error("Error fetching models:", err);
            setLoadingMcpData(false);
        } finally {
            setLoadingMcpData(false);
        }
    };

    const fetchMcpLogs = async () => {
        setLogLoading(true)
        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_mcp_logs`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id,
                    org_id,
                    mcp_id,
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            if (result.status === "success" && result.data) {
                setMcpLogs(result.data || []);
            }
        } catch (err) {
            setMcpLogs([{ "id": "SysError", "account": "System", "message": "Error loading logs...", "severity": 2 }])
            console.error("Error fetching ability logs:", err);
            setLogLoading(false)
        } finally {
            setTimeout(() => {
                setLogLoading(false)
            }, 1000);
        }
    };

    const startRunTest = async () => {
        setLoadingRunTest(true)
        try {
            const response = await fetch(`${GEN_AI_LINK}/run_test_ai`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id,
                    org_id,
                    mcp_id,
                    chatId
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            fetchDataLogs()
            fetchMcpData()
        } catch (err) {
            console.error("Error runnint test:", err);
            setLoadingRunTest(false)
        } finally {
            setTimeout(() => {
                setLoadingRunTest(false)
            }, 500);
        }
    };

    const streamMcpStatus = (mcp_id) => {
        if (!mcp_id) return;
        setStatusMcp("LOADING")
        if (socketRef.current) {
            socketRef.current.disconnect();
            socketRef.current = null;
        }

        const socket = io(REACT_APP_GEN_BACKEND_LINK_STREAMING, {
            transports: ["websocket"],
        });

        socketRef.current = socket;

        socket.on("connect", () => {
            socket.emit("subscribe_mcp_status", { mcp_id: mcp_id });
        });

        socket.on("mcp_status", (data) => {
            if (data?.status) {
                setStatusMcp(data.status);
            }
        });

        socket.on("connect_error", (err) => {
            console.error("Socket connection error:", err);
            setStatusMcp("DEPLOYED");
        });

        socket.on("disconnect_mcp_status", () => {
            console.log("Socket disconnected");
        });
    };

    const streamMcpSteps = (chatId) => {
        setSteps({})
        if (!chatId) return;
        if (socketRef.current) {
            socketRef.current.disconnect();
            socketRef.current = null;
        }

        const socket = io(REACT_APP_GEN_BACKEND_LINK_STREAMING, {
            transports: ["websocket"],
        });

        socketRef.current = socket;

        const id_to_send = `${chatId}-${account_id}`
        socket.on("connect", () => {
            socket.emit("subscribe_steps", {
                chatId: id_to_send,
                isAutotestEnabled
            });
        });

        socket.on("steps", (data) => {
            if (data?.steps != "NOT_FOUND") {
                setSteps(data?.steps)
            }
        });

        socket.on("connect_error", (err) => {
            console.error("Socket connection error:", err);
        });

        socket.on("disconnect_steps", () => {
            console.log("Socket disconnected");
        });
    };

    const handleSave = async () => {
        const filesArray = Object.keys(files).map((filename) => ({
            filename,
            content: files[filename]
        }));
        setLoadingSave(true)
        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/save_mcp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    account_id,
                    org_id,
                    mcp_id,
                    mcp_name: mcpData.mcp_name,
                    description: mcpData.description,
                    source_url: mcpData.source_url,
                    organization_ids: mcpData.organization_ids,
                    category: mcpData.category,
                    transport_type: mcpData.transport_type,
                    tools: mcpData.tools,
                    license: mcpData.license,
                    files: filesArray,
                    runtime: runtime,
                    region: mcpData.region
                }),
            });

            const result = await response.json();

            if (result.error == "not_authorized") {
                setPopupMex({ active: true, type: "error", message: "Organization auth..." })
                setTimeout(() => {
                    setPopupMex({ active: false, type: null, message: "" })
                    navigate(`/e8console/organizations`)
                }, 5000);
            }

            if (!response.ok) throw new Error("Failed to deploy MCP");

            if (result.status == "success") {
                setHasUnsavedChanges(false)
                skipPromptRef.current = true;

                const new_drafted_version = result.new_drafted_version
                setDraftedVersion(new_drafted_version)
                const mainUrl = `/e8console/organizations/${org_id}/abilities`;

                const targetPath =
                    runtime === "python"
                        ? `${mainUrl}/ability-builder/python/${result.mcp_id}/${new_drafted_version}`
                        : `${mainUrl}/ability-builder/javascript/${result.mcp_id}/${new_drafted_version}`;

                navigate(targetPath);
            }
        } catch (err) {
            setLoadingSave(false)
        } finally {
            setLoadingSave(false)
        }
    };

    const handleOpenSaveModal = () => {
        const filesArray = Object.keys(files).map((filename) => ({
            filename,
            content: files[filename]
        }));

        setFilesToSave(filesArray);
        setOpenSaveModal(true);
    }

    const toggleOpenVersions = () => {
        openVersions ? setOpenVersions(false) : setOpenVersions(true)
    }

    const handleOpenVersion = (version) => {
        setOpenManually(true)
        setHasUnsavedChanges(
            JSON.stringify(files) !== JSON.stringify(originalFiles)
        );
        skipPromptRef.current = false;
        navigate(`/e8console/organizations/${org_id}/abilities/ability-builder/${runtime}/${mcp_id}/${version}`)
    }

    const handleContinueWithoutSaving = () => {
        skipPromptRef.current = true;

        setOpenAlert(false);

        if (pendingNavigation) {
            pendingNavigation();
        }

        setPendingNavigation(null);
    };

    function usePrompt(when, skipPromptRef, setOpenAlert, setPendingNavigation) {
        const navigator = useContext(UNSAFE_NavigationContext).navigator;

        useEffect(() => {
            if (!when) return;

            const originalPush = navigator.push;

            navigator.push = (...args) => {
                if (skipPromptRef.current) {
                    originalPush(...args);
                    return;
                }

                setPendingNavigation(() => () => originalPush(...args));
                setOpenAlert(true);
            };

            return () => {
                navigator.push = originalPush;
            };
        }, [navigator, when]);
    }

    const handleInput = () => {
        const el = inputRef.current;
        if (!el) return;

        el.style.height = "auto";

        const maxHeight = 200;

        if (el.scrollHeight <= maxHeight) {
            el.style.overflowY = "hidden";
            el.style.height = el.scrollHeight + "px";
        } else {
            el.style.height = maxHeight + "px";
            el.style.overflowY = "auto";
        }
    };


    useEffect(() => {
        setEditorTheme(resolveTheme());
        const refreshTheme = () => setEditorTheme(resolveTheme());

        window.addEventListener("storage", refreshTheme);
        window.addEventListener("pe8-theme-changed", refreshTheme);

        const systemQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const handleSystemChange = () => {
            if (localStorage.getItem("pe8-theme-preference") === "system") refreshTheme();
        };
        systemQuery.addEventListener("change", handleSystemChange);

        return () => {
            window.removeEventListener("storage", refreshTheme);
            window.removeEventListener("pe8-theme-changed", refreshTheme);
            systemQuery.removeEventListener("change", handleSystemChange);
        };
    }, [resolveTheme]);


    useEffect(() => {
        function hideError(e) {
            if (e.message === "ResizeObserver loop completed with undelivered notifications.") {
                const resizeObserverErrDiv = document.getElementById("webpack-dev-server-client-overlay-div");
                const resizeObserverErr = document.getElementById("webpack-dev-server-client-overlay");
                if (resizeObserverErr) resizeObserverErr.setAttribute("style", "display: none");
                if (resizeObserverErrDiv) resizeObserverErrDiv.setAttribute("style", "display: none");
            }
        }
        window.addEventListener("error", hideError);
        return () => window.removeEventListener("error", hideError);
    }, []);

    const fetchDataLogs = () => {
        fetchMcpData()
        fetchMcpLogs()
    }

    useEffect(() => {
        fetchMcpData()
        fetchMcpLogs()
        streamMcpStatus(mcp_id)
    }, [account_id, mcp_id, version_n]);

    useEffect(() => {
        if (statusMcp == "DEPLOYING") {
            setOpenTerminal(true)
        }
        if (statusMcp == "DEPLOYED") {
            fetchMcpData()
            fetchMcpLogs()
            streamMcpSteps(chatId)
        }
    }, [statusMcp]);

    useEffect(() => {
        if (steps?.[4]?.status && steps[4].status == "SUCCESS") {
            fetchMcpData()
            fetchMcpLogs()
        }
    }, [steps])


    useEffect(() => {
        streamMcpSteps(chatId);
    }, [account_id, chatId, messages, version_n]);

    useEffect(() => {
        fetchChatHistory()
    }, [account_id])

    // useEffect(() => {
    //     fetchSingleChat()
    // }, [account_id, chatId])

    useEffect(() => {
        setHasUnsavedChanges(
            JSON.stringify(files) !== JSON.stringify(originalFiles)
        );
    }, [files, originalFiles]);

    usePrompt(
        hasUnsavedChanges,
        skipPromptRef,
        setOpenAlert,
        setPendingNavigation
    );

    return (
        <div className="main_mcp_cont">
            <PopupMex active={popupMex.active} type={popupMex.type} message={popupMex.message} />
            <div className={`main_mcp_cont_chat ${openAiChat ? "chat_shrunk" : ""}`}>
                <div className="main_mcp_cont_chat_header">
                    <div className="main_mcp_cont_chat_btn_cont" >

                        {loadingMcpData ? (
                            <button className="main_mcp_cont_chat_p fade-in main_mcp_cont_chat_btn_loading"></button>
                        ) : (
                            mcpData.mcp_name ? (
                                <p className="main_mcp_cont_chat_title fade-in" onClick={() => setActiveFile("")}>{mcpData.mcp_name}</p>
                            ) : (
                                <p className="main_mcp_cont_chat_title" onClick={() => setActiveFile("")}>{t.abilities.abilities_builder}</p>
                            )
                        )}
                    </div>
                    <div className="main_mcp_cont_chat_btn_cont">
                        {mcpData.mcp_name ? (
                            <>

                                <button className="main_mcp_cont_chat_btn fade-in" onClick={() => setOpenInfoModal(true)}>
                                    <BsInfo />
                                </button>
                                <button className={`main_mcp_cont_chat_btn fade-in ${(statusMcp == "DEPLOYING" || loadingSave) ? "main_mcp_cont_chat_btn_loading" : ""}`} onClick={handleOpenPublishModal} disabled={(statusMcp == "DEPLOYING" || loadingSave) ? true : false}>
                                    {(statusMcp == "DEPLOYING") ? (
                                        <span className="fade-in"><Spinner loading={true} /> {t.abilities.publishing}</span>
                                    ) : (
                                        <span className="fade-in"><BiRocket /> {t.abilities.publish}</span>
                                    )}
                                </button>
                            </>
                        ) : (
                            <>
                                <button className="main_mcp_cont_chat_btn fade-in main_mcp_cont_chat_btn_loading"></button>
                            </>
                        )}
                        <button className="main_mcp_cont_chat_btn fade-in" onClick={toggleCodeView}>
                            {openAiChat ? <TbLayoutSidebarLeftExpand /> : <TbLayoutSidebarRightExpandFilled />}
                        </button>
                    </div>
                </div>

                <div className="mcp_editor_sidebar_cont">
                    <div className="filetree_editor">
                        <div className="filetree_editor_header">
                            {mcpData.mcp_name && (
                                <>
                                    <button className={`filetree_editor_header_btn fade-in ${(statusMcp == "DEPLOYING" || loadingSave) ? "filetree_editor_header_btn_loading" : ""}`} onClick={handleSave} disabled={(statusMcp == "DEPLOYING" || loadingSave)}>
                                        {loadingSave ? (
                                            <Spinner loading={true} />
                                        ) : (
                                            <BiSave />
                                        )}
                                    </button>
                                    <button className={`filetree_editor_header_btn fade-in`} onClick={handleOpenSaveModal} disabled={(loadingSave)}><VscSettingsGear /></button>
                                </>
                            )}
                        </div>
                        <div className="filetree_body">
                            {loadingMcpData ? (
                                <CodeTreeLoader files={files} />
                            ) : (
                                Object.keys(files).map((file) => (
                                    <div
                                        key={file}
                                        className={`file_item fade-in ${activeFile === file ? "active" : ""}`}
                                        onClick={() => setActiveFile(file)}
                                    >
                                        {file.endsWith(".py") && <FaPython />}
                                        {file.endsWith(".js") && <FaNodeJs />}
                                        {file.endsWith(".json") && <VscJson />}
                                        {file.endsWith(".txt") && <FiFileText />}
                                        {file.startsWith("Dockerfile") && <FaDocker />}
                                        {!file.match(/\.(py|js|json|txt)$/) && file !== "Dockerfile" && <FiFileText />}

                                        <span >{file}</span>
                                    </div>
                                ))
                            )}
                        </div>
                        <div className={`file_tree_versions ? ${openVersions ? "" : "file_tree_versions_closed"}`}>
                            <div className="file_tree_versions_header" >
                                {/* <i className={`fa-solid fa-chevron-${openVersions ? "down" : "right"}`}></i> */}
                                <p className="file_tree_versions_title">{t.abilities.versioning}</p>
                            </div>

                            {versions.length == 0 ? (
                                <div className="version_item_cont">
                                    {loadingMcpData ? (
                                        <VersioningLoader length={3} version="" />
                                    ) : (
                                        <div
                                            className={`version_item fade-in ${version_n == 0 ? "version_item_selected" : ""}`}
                                            onClick={() => handleOpenVersion(0)}
                                        >
                                            v0
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="version_item_cont">
                                    {loadingMcpData && (Number(mcpData.drafted_version) + 1) == draftedVersion && (
                                        <VersioningLoader length={1} version={`v${Number(mcpData.drafted_version) + 1}`} />
                                    )}

                                    {loadingSave && (
                                        <div className="version_item no_drop">
                                            v{Number(mcpData.drafted_version) + 1}
                                            <span className="version_item_span fade-in">
                                                {t.abilities.saving}
                                                <Spinner loading={true} />
                                            </span>
                                        </div>
                                    )}
                                    {versions
                                        .slice()
                                        .sort((a, b) => b - a)
                                        .map((version) => (
                                            <div
                                                className={`version_item  ${version_n == version ? "version_item_selected" : ""}`}
                                                onClick={() => handleOpenVersion(version)}
                                            >
                                                v{version}
                                                <div className="version_item_span_cont">

                                                    {(mcpData.version == version && statusMcp == "DEPLOYED") && (
                                                        <span className="version_item_span fade-in">
                                                            {t.abilities.published}
                                                        </span>
                                                    )}


                                                    {(mcpData.version == version && statusMcp == "DEPLOYING") && (
                                                        <span className="version_item_span fade-in">
                                                            {/* PUBLISHING */}
                                                            <Spinner loading={true} />
                                                        </span>
                                                    )}

                                                    {(version_n == version && (loadingMcpData && (statusMcp != "DEPLOYING" ))) && (
                                                        <span className="version_item_span">
                                                            <Spinner loading={true} />
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={`mcp_editor_wrapper ${editorTheme == "vs-dark" ? "mcp_editor_wrapper_dark" : "mcp_editor_wrapper_light"}`}>

                        {/* {loadingMcpData ? (
                            <CodeLinesLoader />
                        ) : ( */}
                        {files[activeFile] ? (
                            <div className="editor_cont">
                                <Editor
                                    key={`${editorTheme}-${runtime}-${mcp_id}`}
                                    language={getEditorLanguage()}
                                    theme={editorTheme}
                                    value={files[activeFile] || ""}
                                    onChange={handleEditorChange}
                                    options={{
                                        fontSize: 12,
                                        minimap: { enabled: false },
                                        automaticLayout: true,
                                        fontFamily: "'JetBrains Mono', monospace",
                                        padding: { top: 20 },
                                    }}
                                />
                            </div>
                        ) : (
                            <CircleBackground>
                                <div className="empty_file_editor">
                                    <div className="empty_file_editor_desc_cont">
                                        <p className="empty_file_editor_title">{t.abilities.welcome_title}</p>
                                        <p className="empty_file_editor_title_desc">
                                            {t.abilities.welcome_subtitle}
                                        </p>
                                    </div>
                                </div>
                            </CircleBackground>
                        )}
                        {/* )} */}

                        <EditorTerminal
                            openTerminal={openTerminal}
                            setOpenTerminal={setOpenTerminal}
                            fetchMcpLogs={fetchMcpLogs}
                            logLoading={logLoading}
                            statusMcp={statusMcp}
                            mcpData={mcpData}
                            mcpLogs={mcpLogs}
                            loadingSave={loadingSave}
                        />
                    </div>
                </div>
            </div>

            <AgentAiChat
                type={"MCP"}
                account_id={account_id}
                openAiChat={openAiChat}
                messages={messages}
                chatEndRef={chatEndRef}
                handleSendMessage={handleSendMessage}
                handleStopMessage={handleStopMessage}
                input={input}
                setInput={setInput}
                handleKeyDown={handleKeyDown}
                isLoading={isLoading}
                errorAi={errorAi}
                chatData={chatData}
                setChatId={setChatId}
                chatId={chatId}
                loadingChats={loadingChats}
                loadingChatHistory={loadingChatHistory}
                fetchChatHistory={fetchChatHistory}
                steps={steps}
                fetchSingleChat={fetchSingleChat}
                isSearchEnabled={isSearchEnabled}
                setIsSearchEnabled={setIsSearchEnabled}
                isAutotestEnabled={isAutotestEnabled}
                setIsAutotestEnabled={setIsAutotestEnabled}
                statusMcp={statusMcp}
                inputRef={inputRef}
                handleInput={handleInput}
                selectedModel={selectedModel}
                setSelectedModel={setSelectedModel}

                mcpData={mcpData}
                setSelectedMcp={setSelectedMcp}
                handleOpenConnect={handleOpenConnect}
                handleOpenDisconnect={handleOpenDisconnect}
                setOpenErrDepModal={setOpenErrDepModal}
                fetchMcpData={fetchMcpData}
                loadingMcpData={loadingMcpData}
                startRunTest={startRunTest}
                loadingRunTest={loadingRunTest}
            />

            <PublishMcpModal
                setPopupMex={setPopupMex}
                account_id={account_id}
                show={openPublishModal}
                handleClose={() => setOpenPublishModal(false)}
                runtime={runtime}
                mcpData={mcpData}
                fetchMcpData={fetchMcpData}
                streamMcpStatus={streamMcpStatus}
                singleOrgData={singleOrgData}
            />

            <SaveMcpModal
                setPopupMex={setPopupMex}
                account_id={account_id}
                show={openSaveModal}
                handleClose={() => setOpenSaveModal(false)}
                mcpData={mcpData}
                runtime={runtime}
                files={filesToSave}
                setLoadingSave={setLoadingSave}
                setDraftedVersion={setDraftedVersion}
                setOpenConfirmDeleteModal={setOpenConfirmDeleteModal}
            />

            <InfoModal
                show={openInfoModal}
                handleClose={() => setOpenInfoModal(false)}
                mcpData={mcpData}
            />

            <UnsavedChangesModal
                show={openAlert}
                handleClose={() => setOpenAlert(false)}
                handleSave={handleSave}
                loadingSave={loadingSave}
                handleContinueWithoutSaving={handleContinueWithoutSaving}
            />

            <ConfirmDeleteMcpModal
                setPopupMex={setPopupMex}
                account_id={account_id}
                show={openConfirmDeleteModal}
                handleClose={() => setOpenConfirmDeleteModal(false)}
                org_id={org_id} 
                mcp_id={mcp_id}
            />

            <ConnectCustomMcpModal
                account_id={account_id}
                userData={userData}
                show={openCustomModal}
                handleClose={() => setOpenCustomModal(false)}
                selectedMCP={selectedMCP}
                fetchData={fetchDataLogs}
                setMcpData={setMcpData}
            />
            <DisconnectCustomMcpModal
                account_id={account_id}
                userData={userData}
                show={showCustomDisconnectModal}
                handleClose={() => setShowCustomDisconnectModal(false)}
                selectedMCP={selectedMCP}
                fetchData={fetchDataLogs}
            />
        </div>
    );
};

export default McpBuilder;